import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, map, switchMap, timer, Subscription, EMPTY, catchError, of, finalize } from 'rxjs';
import {
  User,
  AuthResponse,
  LoginCredentials,
  RegisterData,
} from '../models/user.model';
import { environment } from '../../../environments/environment';
import { AccountService } from './account.service';
import { PageLifecycleService } from './page-lifecycle.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);

  private readonly API_URL = environment.apiUrl || 'http://localhost:8000/api';
  private readonly TOKEN_KEY = 'fluxtail-auth-token';
  private readonly SOCIAL_NONCE_KEY = 'fluxtail-social-browser-nonce';
  private readonly RETURN_URL_KEY = 'fluxtail-auth-return-url';
  private readonly VERIFICATION_DISMISSAL_PREFIX = 'fluxtail-email-verification-dismissed:';
  private readonly SESSION_CHECK_INTERVAL = 5 * 60 * 1000;
  private currentUser: User | null = null;
  private accountService = inject(AccountService);
  private pageLifecycle = inject(PageLifecycleService);
  private sessionCheckSubscription: Subscription | null = null;
  private lifecycleSubscription: Subscription | null = null;
  private lifecycleInitialized = false;

  constructor() {
    this.lifecycleSubscription = this.pageLifecycle.background$.subscribe((isBackground) => {
      if (!this.lifecycleInitialized) {
        this.lifecycleInitialized = true;
        return;
      }

      if (!isBackground && this.isAuthenticated()) {
        this.restartSessionHeartbeat(true);
      }
    });

    if (this.isAuthenticated()) {
      this.bootstrapAuthenticatedSession();
    }
  }

  /**
   * Register a new user
   */
  register(data: RegisterData): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.API_URL}/auth/register`, data)
      .pipe(switchMap((response) => this.initializeSessionAfterAuth(response)));
  }

  /**
   * Login with email and password
   */
  login(credentials: LoginCredentials): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.API_URL}/auth/login`, credentials)
      .pipe(switchMap((response) => this.initializeSessionAfterAuth(response)));
  }

  socialAuthConfig(): Observable<{
    providers: ('google' | 'github')[];
    signup_enabled: boolean;
  }> {
    return this.http.get<{
      providers: ('google' | 'github')[];
      signup_enabled: boolean;
    }>(`${this.API_URL}/auth/social/config`);
  }

  async startSocialAuth(provider: 'google' | 'github', returnUrl?: string): Promise<void> {
    if (returnUrl) {
      this.rememberReturnUrl(returnUrl);
    }

    const bytes = crypto.getRandomValues(new Uint8Array(32));
    const browserNonce = btoa(String.fromCharCode(...bytes))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
    const digest = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(browserNonce),
    );
    const nonceHash = Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');

    sessionStorage.setItem(this.SOCIAL_NONCE_KEY, browserNonce);
    const backendUrl = this.API_URL.replace(/\/api\/?$/, '');
    window.location.assign(
      `${backendUrl}/auth/social/${provider}/redirect?nonce_hash=${encodeURIComponent(nonceHash)}`,
    );
  }

  exchangeSocialTicket(ticket: string): Observable<AuthResponse> {
    const browserNonce = sessionStorage.getItem(this.SOCIAL_NONCE_KEY);
    if (!browserNonce) {
      throw new Error('Social login session expired. Please try again.');
    }

    return this.http
      .post<AuthResponse>(`${this.API_URL}/auth/social/exchange`, {
        ticket,
        browser_nonce: browserNonce,
      })
      .pipe(
        finalize(() => sessionStorage.removeItem(this.SOCIAL_NONCE_KEY)),
        switchMap((response) => this.initializeSessionAfterAuth(response)),
      );
  }

  rememberReturnUrl(url: string): void {
    if (url.startsWith('/') && !url.startsWith('//')) {
      sessionStorage.setItem(this.RETURN_URL_KEY, url);
    }
  }

  consumeReturnUrl(fallback = '/dashboard'): string {
    const url = sessionStorage.getItem(this.RETURN_URL_KEY);
    sessionStorage.removeItem(this.RETURN_URL_KEY);
    return url && url.startsWith('/') && !url.startsWith('//') ? url : fallback;
  }

  dismissEmailVerification(userId: string): void {
    sessionStorage.setItem(`${this.VERIFICATION_DISMISSAL_PREFIX}${userId}`, '1');
  }

  isEmailVerificationDismissed(userId: string): boolean {
    return sessionStorage.getItem(`${this.VERIFICATION_DISMISSAL_PREFIX}${userId}`) === '1';
  }

  /**
   * Fetch current authenticated user
   */
  fetchUser(): Observable<{ user: User }> {
    return this.http.get<{ user: User }>(`${this.API_URL}/auth/me`).pipe(
      tap((response) => {
        this.currentUser = response.user;
      }),
    );
  }

  /**
   * Get user (fetch if not cached)
   */
  getUser(): Observable<User | null> {
    if (this.currentUser) {
      return new Observable((observer) => {
        observer.next(this.currentUser);
        observer.complete();
      });
    }
    return this.fetchUser().pipe(
      tap((response) => (this.currentUser = response.user)),
      map((response) => response.user),
    );
  }

  /**
   * Logout the current user
   */
  logout(): void {
    this.stopSessionHeartbeat();
    this.http.post(`${this.API_URL}/auth/logout`, {}).subscribe({
      next: () => {
        this.finishLogout();
      },
      error: () => {
        this.finishLogout();
      },
    });
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Get the current user value
   */
  getCurrentUser(): User | null {
    return this.currentUser;
  }

  /**
   * Get the authentication token
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Request password reset email
   */
  forgotPassword(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.API_URL}/auth/forgot-password`, { email });
  }

  /**
   * Reset password with token
   */
  resetPassword(
    token: string,
    email: string,
    password: string,
    password_confirmation: string,
  ): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.API_URL}/auth/reset-password`, {
      token,
      email,
      password,
      password_confirmation,
    });
  }

  /**
   * Verify email with signed URL
   */
  verifyEmail(url: string): Observable<{ message: string }> {
    return this.http.get<{ message: string }>(url);
  }

  /**
   * Resend verification email
   */
  resendVerification(): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.API_URL}/auth/resend-verification`, {});
  }

  /**
   * Update password (authenticated user)
   */
  updatePassword(
    current_password: string,
    new_password: string,
    new_password_confirmation: string,
  ): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.API_URL}/auth/update-password`, {
      current_password,
      new_password,
      new_password_confirmation,
    });
  }

  private startSessionHeartbeat(immediate = false): void {
    if (this.sessionCheckSubscription && !this.sessionCheckSubscription.closed) {
      return;
    }

    const initialDelay = immediate ? 0 : this.SESSION_CHECK_INTERVAL;
    this.sessionCheckSubscription = timer(initialDelay, this.SESSION_CHECK_INTERVAL)
      .pipe(
        switchMap(() =>
          this.fetchUser().pipe(
            catchError((err) => {
              if (err.status === 401) {
                console.warn('[Auth] Session heartbeat detected expiration. Logging out.');
                this.handleSessionExpired();
              }
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe();
  }

  private stopSessionHeartbeat(): void {
    if (this.sessionCheckSubscription) {
      this.sessionCheckSubscription.unsubscribe();
      this.sessionCheckSubscription = null;
    }
  }

  private restartSessionHeartbeat(immediateSessionCheck = false): void {
    this.stopSessionHeartbeat();
    this.startSessionHeartbeat(immediateSessionCheck);
  }

  private bootstrapAuthenticatedSession(): void {
    this.fetchUser().subscribe({
      error: (err) => {
        if (err.status === 401) {
          this.handleSessionExpired();
        }
      },
    });
    this.accountService.loadMemberships().subscribe();
    this.restartSessionHeartbeat();
  }

  private performLocalLogout(): void {
    this.stopSessionHeartbeat();
    this.currentUser = null;
    localStorage.removeItem(this.TOKEN_KEY);
    this.accountService.clearActiveAccount();
  }

  private handleSessionExpired(): void {
    this.performLocalLogout();
    window.location.href = '/login?session=expired';
  }

  private finishLogout(): void {
    this.performLocalLogout();
    window.location.href = '/login';
  }

  private initializeSessionAfterAuth(response: AuthResponse): Observable<AuthResponse> {
    this.setToken(response.token);
    this.currentUser = response.user;
    sessionStorage.removeItem(`${this.VERIFICATION_DISMISSAL_PREFIX}${response.user.id}`);

    return this.accountService.loadMemberships().pipe(
      catchError(() => of({ memberships: [] })),
      switchMap(() =>
        this.fetchUser().pipe(
          map(() => response),
          catchError(() => of(response)),
        ),
      ),
      tap(() => this.restartSessionHeartbeat()),
    );
  }

  private setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }
}
